import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Grid, CircularProgress, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { toggleFavorite } from '../../actions/users';
import { startChat } from '../../actions/chat';
import { getPublicServiceDetail, getLatestServices } from '../../actions/profile';
import { urlImg, isEmpty } from '../../components/common/helpers';

import SectionService from '../../components/services/SectionServices';
import ServiceImages from '../../components/services/detail/ServiceImages';
import ServiceHeaderInfo from '../../components/services/detail/ServiceHeaderInfo';
import ServiceReviews from '../../components/services/detail/ServiceReviews';
import ServiceContent from '../../components/services/detail/ServiceContent';
import ServiceLocation from '../../components/services/detail/ServiceLocation';

const PublicServiceDetail = () => {
  const { userId, serviceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  const [service, setService] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ownerUser, setOwnerUser] = useState(null);
  const [latestServices, setLatestServices] = useState([]);

  const authUser = useSelector(state => state.user.user);
  const isOwner = authUser && String(authUser._id) === String(userId);
  const favorites = useSelector(state => state.user.user.favorites || []);
  const isAuthenticated = useSelector(state => state.user.loading === false && state.user.user._id);

  const isFavorite = favorites.some(favId => favId.toString() === serviceId.toString());

  const onToggleFavorite = () => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    dispatch(toggleFavorite(serviceId));
  };

  const handleQuote = () => {
    console.log('--- Debug Cotizar (ServiceHeader) ---');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('userId (recipient):', userId);
    console.log('isOwner:', isOwner);

    if (!isAuthenticated) {
      console.warn('Usuario no autenticado');
      navigate('/');
      return;
    }
    if (userId && !isOwner) {
      console.log('Iniciando chat desde ServiceHeader...');
      const initialMsg = `Hola, me interesa contratar el servicio de "${service.nombre}". Vi tu perfil y me gustaría cotizar. ¿Podrías darme un presupuesto?`;
      dispatch(startChat(userId, serviceId, 'service', initialMsg, `${userId}_${serviceId}`));
    } else {
      console.warn('No se puede iniciar chat: falta userId o es el dueño');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchService = async () => {
      try {
        const data = await getPublicServiceDetail(userId, serviceId);
        if (data && data.service) {
          setService(data.service);
          setProfile(data.profile);
          setOwnerUser(data.ownerUser);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    const fetchLatestServices = async () => {
      const data = await getLatestServices();
      const formattedServices = data.map(serv => {
        let finalAvatarUrl = "";
        if (!isEmpty(serv.authorAvatar)) {
          const avatarStr = serv.authorAvatar.toString();
          if (avatarStr.startsWith("http")) {
            finalAvatarUrl = avatarStr;
          } else if (avatarStr.startsWith("//")) {
            finalAvatarUrl = avatarStr.replace(/^\/\//, "https://");
          } else {
            finalAvatarUrl = `${urlImg}/w_150,h_150,c_fill/users/${serv.userId}/${serv.authorAvatar}`;
          }
        }

        return {
          ...serv,
          authorAvatar: finalAvatarUrl,
          image: !isEmpty(serv.image) ? `${urlImg}/w_400,h_250,c_fill/servicios/${serv._id}/${serv.image}` : ""
        };
      });
      setLatestServices(formattedServices.filter(s => s._id !== serviceId));
    };

    fetchService();
    fetchLatestServices();
  }, [userId, serviceId]);

  useEffect(() => {
    if (error) {
      navigate('/', { replace: true });
    }
  }, [error, navigate]);

  if (loading || error || !service) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F8FAFC' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 8, pt: 2, mt: 10 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIosIcon />}
        onClick={() => navigate(-1)}
        sx={{
          textTransform: 'none',
          color: '#0E2465',
          fontWeight: 'bold',
          mb: 2,
          '&:hover': { bgcolor: 'transparent', opacity: 0.8 }
        }}
      >
        Atrás
      </Button>

      {/* Images Grid */}
      <ServiceImages id={service._id} images={service.img} />

      {/* Header Info (Title, Price, Stats) */}
      <ServiceHeaderInfo 
        service={service} 
        isOwner={isOwner} 
        isFavorite={isFavorite}
        onToggleFavorite={onToggleFavorite}
        onQuote={handleQuote}
      />

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 5 }}>
          <ServiceLocation service={service} profile={profile} user={ownerUser} />
          <ServiceReviews
            servicioId={service._id}
            nombreProveedor={ownerUser ? `${ownerUser.name} ${ownerUser.apellido}` : (profile?.nombre || "Usuario")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <ServiceContent
            service={service}
            profile={profile}
            user={ownerUser}
          />
        </Grid>
      </Grid>

      {/* Featured Services Carousel */}
      <Box sx={{ mt: 8 }}>
        <SectionService
          title="Servicios destacados"
          subtitle="Otros servicios que podrían interesarte"
          services={latestServices}
          favoriteService={(srv) => console.log("Favorite ", srv)}
          irA={() => navigate("/home")}
          onVerDetalle={(srv) => navigate(`/servicio/${srv.userId}/${srv._id}`)}
          showPriece
        />
      </Box>
    </Box>
  );
};

export default PublicServiceDetail;
